import { CARD_COUNT_LABEL } from "./catalog-copy";
/**
 * Page titles + meta descriptions — ONE table for the client (document.title + the
 * description/og/twitter tags on route change) and the server (`server/lib/og.ts`, what
 * crawlers see). Ported from legacy `bday/seo_config.json` (2026-09-29, PRD 13 URL parity)
 * and lightly rewritten; legacy's `title` = browser tab, `meta_title` = og/twitter title.
 *
 * Match order = array order; first hit wins (put exact routes before prefixes).
 */

/**
 * Environment tag derived from the ORIGIN (Nick 2026-10-16; replaces the hand-flipped
 * "[v2] " constants here and in server/lib/helpers.ts): the apex and `www` are untagged;
 * any other host is tagged with its first label — "[v2] " for v2.birthdayalarm.com,
 * "[staging] ", "[local] " for localhost / an IP. Used for browser-tab titles (client,
 * from window.location) and email subjects (server, from BASE_URL). The cutover moves
 * BASE_URL / the site to the apex, so the tags disappear on their own.
 */
export function originTag(hostname: string): string {
  const h = hostname.trim().toLowerCase();
  if (!h) return "";
  if (h === "birthdayalarm.com" || h === "www.birthdayalarm.com") return "";
  if (h === "localhost" || /^[\d.:]+$/.test(h)) return "[local] ";
  const label = h.endsWith(".birthdayalarm.com") ? h.slice(0, -".birthdayalarm.com".length) : h;
  return `[${label.split(".")[0]}] `;
}

/** The current page's tag (client only; "" when there is no window, e.g. server OG). */
export function titlePrefix(): string {
  const g = globalThis as { location?: { hostname?: string } };
  return originTag(g.location?.hostname ?? "");
}

export const SITE_NAME = "BirthdayAlarm";

export interface SeoEntry {
  match: "exact" | "prefix" | "contains";
  value: string;
  /** Browser-tab title (without the site suffix unless `raw`). */
  title: string;
  /** og:title / twitter:title — defaults to the tab title. */
  metaTitle?: string;
  /** meta description + og:description — defaults to SEO_DEFAULTS.description. */
  description?: string;
  /** `{name}` in title/metaTitle is replaced by the path remainder after a `prefix` match. */
  template?: boolean;
  /** Title is complete as written — do not append " | BirthdayAlarm". */
  raw?: boolean;
  /** Account/private surfaces: crawlers get <meta name="robots" content="noindex">. */
  noindex?: boolean;
}

export const SEO_DEFAULTS = {
  title: `BirthdayAlarm ⏰ - ${CARD_COUNT_LABEL} Online Greeting Cards - send ecards by text or email`,
  metaTitle: `BirthdayAlarm ⏰ - ${CARD_COUNT_LABEL} Online Greeting Cards | Send ecards by text or email`,
  description: `Send beautiful online greeting cards in seconds. Browse ${CARD_COUNT_LABEL} ecards for birthdays, holidays, anniversaries and more, and never miss a birthday with free reminders. Trusted since 2001.`,
};

export const SEO_ROUTES: SeoEntry[] = [
  // ── Public, indexed ──────────────────────────────────────────────────────────
  {
    match: "exact",
    value: "/",
    title: SEO_DEFAULTS.title,
    metaTitle: SEO_DEFAULTS.metaTitle,
    description: SEO_DEFAULTS.description,
    raw: true,
  },
  {
    match: "exact",
    value: "/about",
    title: "Meet the Founders",
    description:
      "BirthdayAlarm was started in 2001 by Michael and Xochi Birch, and still runs on the same idea: remember the date, so you can celebrate.",
  },
  {
    match: "exact",
    value: "/company",
    title: "Company Information",
    description: "Who is behind BirthdayAlarm, where we are, and how to reach us.",
  },
  {
    match: "exact",
    value: "/terms",
    title: "Terms of Use",
    description:
      "BirthdayAlarm's terms of use — the guidelines for using our reminders and ecards.",
  },
  {
    match: "exact",
    value: "/privacy",
    title: "Privacy Policy",
    description:
      "What information BirthdayAlarm collects, how we use it, and the choices you have. We respect your right to privacy.",
  },
  {
    match: "exact",
    value: "/safety",
    title: "Safety & Trust",
    description: "How BirthdayAlarm keeps cards, reminders and your contacts safe.",
  },
  {
    match: "exact",
    value: "/contact",
    title: "Contact Us",
    description: "Need help or have feedback? Contact BirthdayAlarm support — we read everything.",
  },
  {
    match: "prefix",
    value: "/faq",
    title: "Help & FAQ",
    description:
      "Answers to the most common questions about BirthdayAlarm reminders, ecards, group cards and your account.",
  },
  {
    match: "prefix",
    value: "/help",
    title: "Help Center",
    description: "Guides and answers for BirthdayAlarm reminders, ecards and your account.",
  },
  {
    match: "prefix",
    value: "/support",
    title: "Support",
    description: "Get help from the BirthdayAlarm team.",
    noindex: true,
  },
  {
    match: "exact",
    value: "/infographic",
    title: "Birthday Infographic",
    description:
      "Your life in numbers: see your birthday infographic, or send one to someone special for theirs.",
  },
  {
    match: "exact",
    value: "/name",
    title: "How Popular is Your Name",
    description:
      "Find out how popular your name is — rankings by year, meaning and origin, and a few surprising facts.",
  },
  {
    match: "prefix",
    value: "/name/",
    title: "How Popular is the Name {name}",
    template: true,
  },
  {
    match: "exact",
    value: "/happy_birthday",
    title: "Happy Birthday!",
    description: "A birthday greeting from BirthdayAlarm — and a card to send back.",
  },
  // ── Cards / catalog ──────────────────────────────────────────────────────────
  {
    match: "exact",
    value: "/cards",
    title: "Online Greeting Cards",
    description: `Browse ${CARD_COUNT_LABEL} ecards for birthdays, holidays, anniversaries and every occasion. Send by text or email in seconds.`,
  },
  {
    match: "prefix",
    value: "/cards/birthday",
    title: "Birthday Cards",
    description:
      "Free birthday ecards — funny, heartfelt, animated and photo cards. Pick one and send it by text or email in seconds.",
  },
  {
    match: "prefix",
    value: "/birthday/browse",
    title: "Birthday Cards",
    description:
      "Free birthday ecards — funny, heartfelt, animated and photo cards. Pick one and send it by text or email in seconds.",
  },
  {
    match: "prefix",
    value: "/cards/anniversary",
    title: "Anniversary Cards",
    description:
      "Anniversary ecards for every year and every couple. Send one by text or email in seconds.",
  },
  {
    match: "prefix",
    value: "/anniversary/browse",
    title: "Anniversary Cards",
    description:
      "Anniversary ecards for every year and every couple. Send one by text or email in seconds.",
  },
  {
    match: "prefix",
    value: "/cards/holiday",
    title: "Holiday Cards",
    description:
      "Holiday ecards for every celebration on the calendar. Send one by text or email in seconds.",
  },
  {
    match: "prefix",
    value: "/holiday/browse",
    title: "Holiday Cards",
    description:
      "Holiday ecards for every celebration on the calendar. Send one by text or email in seconds.",
  },
  {
    match: "prefix",
    value: "/cards/",
    title: "Online Greeting Cards",
    description: `Browse ${CARD_COUNT_LABEL} ecards for birthdays, holidays, anniversaries and every occasion. Send by text or email in seconds.`,
  },
  { match: "prefix", value: "/card/create", title: "Create a Card", noindex: true },
  { match: "prefix", value: "/card/send", title: "Send a Card", noindex: true },
  { match: "prefix", value: "/card/sign", title: "Sign the Card", noindex: true },
  { match: "prefix", value: "/card/read", title: "You've Got a Card", noindex: true },
  { match: "prefix", value: "/card/share", title: "BirthdayAlarm Card", noindex: true },
  // ── Birthday requests ────────────────────────────────────────────────────────
  {
    match: "contains",
    value: "/birthday/request",
    title: "Share Your Birthday With Me",
    description:
      "Help me build a birthday calendar — share your birthday in seconds. BirthdayAlarm has been the trusted free reminder service since 2001.",
  },
  // ── Auth ─────────────────────────────────────────────────────────────────────
  {
    match: "exact",
    value: "/login",
    title: "Login",
    description: "Log in to your BirthdayAlarm account to manage reminders and cards.",
  },
  {
    match: "exact",
    value: "/register",
    title: "Register",
    description: "Create a free BirthdayAlarm account to add reminders and send ecards.",
  },
  { match: "exact", value: "/forgot", title: "Forgot Password", noindex: true },
  { match: "exact", value: "/forgot-password", title: "Forgot Password", noindex: true },
  { match: "exact", value: "/verify", title: "Verify Your Email", noindex: true },
  { match: "exact", value: "/email-verify", title: "Verify Your Email", noindex: true },
  { match: "exact", value: "/unsubscribe", title: "Email Preferences", noindex: true },
  // ── Member area (noindex) ────────────────────────────────────────────────────
  { match: "exact", value: "/home", title: "Home", noindex: true },
  { match: "exact", value: "/reminders", title: "My Reminders", noindex: true },
  { match: "prefix", value: "/reminders/", title: "Add Reminders", noindex: true },
  { match: "exact", value: "/reminders-print-view", title: "My Reminders — Print", noindex: true },
  { match: "exact", value: "/address_book", title: "My Contacts", noindex: true },
  { match: "prefix", value: "/contacts/import", title: "Import Contacts", noindex: true },
  { match: "exact", value: "/calendar", title: "My Calendar", noindex: true },
  { match: "exact", value: "/mycards/sent", title: "My Cards — Sent", noindex: true },
  { match: "exact", value: "/mycards/upcoming", title: "My Cards — Upcoming", noindex: true },
  { match: "exact", value: "/mycards/drafts", title: "My Cards — Drafts", noindex: true },
  { match: "exact", value: "/mycards/received", title: "My Cards — Received", noindex: true },
  { match: "exact", value: "/mycards/signed", title: "My Cards — Signed", noindex: true },
  { match: "exact", value: "/mycards/favorites", title: "My Cards — Favorites", noindex: true },
  { match: "exact", value: "/mycards/hidden", title: "My Cards — Hidden", noindex: true },
  { match: "exact", value: "/mycards/photo", title: "My Photo Cards", noindex: true },
  { match: "exact", value: "/mycards/gift", title: "My Gift Cards", noindex: true },
  { match: "prefix", value: "/mycards", title: "My Cards", noindex: true },
  { match: "exact", value: "/account", title: "My Account", noindex: true },
  { match: "prefix", value: "/account/", title: "My Account", noindex: true },
  { match: "exact", value: "/email_settings", title: "Email Settings", noindex: true },
  { match: "exact", value: "/notifications", title: "Notifications", noindex: true },
  { match: "exact", value: "/payment", title: "Payment Info", noindex: true },
  { match: "exact", value: "/change_password", title: "Change Password", noindex: true },
  { match: "prefix", value: "/admin", title: "Admin", noindex: true },
];

export interface ResolvedSeo {
  /** Browser-tab title, site suffix applied, WITHOUT the [v2] prefix. */
  title: string;
  metaTitle: string;
  description: string;
  noindex: boolean;
  /** True when a table row matched (false = defaults). */
  matched: boolean;
}

function withSite(title: string): string {
  return `${title} | ${SITE_NAME}`;
}

/** Look a pathname up in the table. Query strings are ignored by callers (pass `pathname`). */
export function resolveSeo(pathname: string): ResolvedSeo {
  const path = pathname.replace(/\/+$/, "") || "/";
  for (const e of SEO_ROUTES) {
    const hit =
      e.match === "exact"
        ? path === e.value
        : e.match === "prefix"
          ? path.startsWith(e.value)
          : path.includes(e.value);
    if (!hit) continue;
    let title = e.title;
    let metaTitle = e.metaTitle ?? e.title;
    if (e.template && e.match === "prefix") {
      const name = decodeURIComponent(path.slice(e.value.length).split("/")[0] ?? "");
      const pretty = name ? name.charAt(0).toUpperCase() + name.slice(1) : "";
      title = title.replace("{name}", pretty);
      metaTitle = metaTitle.replace("{name}", pretty);
    }
    return {
      title: e.raw ? title : withSite(title),
      metaTitle: e.raw ? metaTitle : withSite(metaTitle),
      description: e.description ?? SEO_DEFAULTS.description,
      noindex: e.noindex === true,
      matched: true,
    };
  }
  return {
    title: SEO_DEFAULTS.title,
    metaTitle: SEO_DEFAULTS.metaTitle,
    description: SEO_DEFAULTS.description,
    noindex: false,
    matched: false,
  };
}

/** Browser-tab title for a page-supplied title (usePageTitle) — same shape as the table. */
export function tabTitle(pageTitle: string | null | undefined): string {
  return `${titlePrefix()}${pageTitle ? withSite(pageTitle) : SEO_DEFAULTS.title}`;
}
